import { useQuery } from '@tanstack/react-query';
import type { InferResponseType } from 'hono/client';
import { Printer, MapPin } from 'lucide-react';
import { client } from '@/lib/api';
import { formatDateInZone, formatTimeInZone } from '@/lib/timezone';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { RichBody } from './RichBody';

type CombinedRow = InferResponseType<
  typeof client.api.events.combined.$get,
  200
>[number];

interface CombinedDocViewProps {
  projectId: string;
}

// Group events by their start *date* in the event's own timezone, so a
// Welcome Dinner on May 21 (PT) doesn't slip into the May 22 group when
// the viewer is in a later zone.
function groupByDate(events: CombinedRow[]) {
  const groups = new Map<string, { label: string; events: CombinedRow[] }>();
  for (const e of events) {
    const dateKey = formatDateInZone(e.startAt, e.timezone, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    const label = formatDateInZone(e.startAt, e.timezone, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
    const existing = groups.get(dateKey);
    if (existing) existing.events.push(e);
    else groups.set(dateKey, { label, events: [e] });
  }
  return Array.from(groups.values());
}

export default function CombinedDocView({ projectId }: CombinedDocViewProps) {
  const { data: events, isLoading } = useQuery({
    queryKey: ['events-combined', projectId],
    queryFn: async () => {
      const res = await client.api.events.combined.$get({ query: { projectId } });
      if (!res.ok) throw new Error('Failed to load combined events');
      return res.json();
    },
    enabled: !!projectId,
  });

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading combined doc…</p>;
  if (!events || events.length === 0) {
    return (
      <Card className="rounded-2xl">
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          No events on this project yet.
        </CardContent>
      </Card>
    );
  }

  const groups = groupByDate(events);

  return (
    <Card className="rounded-3xl shadow-ambient overflow-hidden print-page">
      <CardContent className="p-0">
        <div className="px-6 py-4 border-b flex items-center justify-between no-print" data-no-print>
          <p className="text-sm text-muted-foreground">
            {events.length} event{events.length === 1 ? '' : 's'} across {groups.length} day
            {groups.length === 1 ? '' : 's'}
          </p>
          <Button variant="outline" size="sm" onClick={() => window.print()}>
            <Printer className="size-4" />
            Print
          </Button>
        </div>

        {groups.map((group, gi) => (
          <div key={group.label}>
            <div
              className={`px-6 py-3 bg-muted/40 border-y label-caps text-xs text-muted-foreground ${gi > 0 ? 'mt-0' : ''}`}
            >
              {group.label}
            </div>
            {group.events.map((event) => (
              <div key={event.id} className="event-doc-row">
                <div className="px-6 pt-4 pb-2">
                  <h3 className="display-tight text-xl">{event.name}</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {formatTimeInZone(event.startAt, event.timezone)} –{' '}
                    {formatTimeInZone(event.endAt, event.timezone)}
                    {' · '}
                    {event.timezone}
                  </p>
                  {event.location && (
                    <p className="text-xs text-muted-foreground mt-0.5 inline-flex items-center gap-1">
                      <MapPin className="size-3" />
                      {event.location}
                    </p>
                  )}
                </div>
                {event.steps.length === 0 ? (
                  <p className="px-6 py-4 text-sm text-muted-foreground italic">
                    (no steps)
                  </p>
                ) : (
                  event.steps.map((step) => (
                    <div
                      key={step.id}
                      className="event-doc-row grid grid-cols-[5rem_9rem_1fr] gap-x-4 px-6 py-3 border-t"
                    >
                      <div className="text-sm font-medium tabular-nums">
                        {formatTimeInZone(step.startAt, event.timezone)}
                        {step.endAt && (
                          <div className="text-[11px] text-muted-foreground">
                            – {formatTimeInZone(step.endAt, event.timezone)}
                          </div>
                        )}
                      </div>
                      <div className="space-y-0.5">
                        {step.category && (
                          <div className="label-caps text-[11px] text-foreground/80">
                            {step.category}
                          </div>
                        )}
                        <div className="text-sm font-semibold">{step.title}</div>
                      </div>
                      <div className="min-w-0">
                        <RichBody value={step.bodyJson} />
                      </div>
                    </div>
                  ))
                )}
              </div>
            ))}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
